import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { useAuthStore } from "../../store/authStore";
import { host } from "../../api/ApiCalls";
import axios from "axios";

const socket = io("https://fyp-webrtc-server.glitch.me/", {
  autoConnect: false,
});

const LiveSession = () => {
  const { courseId } = useParams();
  const studentId = courseId.substring(courseId.length / 2);
  const courseIdOnly = courseId.substring(0, courseId.length / 2);
  console.log("Course ID:", courseIdOnly);
  console.log("Student ID:", studentId);
  const navigate = useNavigate();
  const { user } = useAuthStore(); // Assuming you have a store for auth
  const [stream, setStream] = useState(null);
  const [peerStreams, setPeerStreams] = useState({}); // Store peer streams in state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [connected, setConnected] = useState(false);
  const [mySocketId, setMySocketId] = useState("");
  const [isReady, setIsReady] = useState(false);
  const [iceServers, setIceServers] = useState([]);
  const [iceServersLoaded, setIceServersLoaded] = useState(false);
  const [course, setCourse] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [teacher, setTeacher] = useState(null);
  const [isTeacher, setIsTeacher] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [pinnedVideo, setPinnedVideo] = useState(null); // 'self' or userId
  const [showChat, setShowChat] = useState(false);
  const [videoRefs, setVideoRefs] = useState({});

  const userVideo = useRef();
  const peersRef = useRef({});
  const messagesEndRef = useRef(null);
  const roomJoined = useRef(false);

  const [sessionTime, setSessionTime] = useState(0); // in seconds

  const getCourseById = async () => {
    try {
      const res = await axios.get(`${host}/api/courses/${courseIdOnly}`, {
        withCredentials: true,
      });
      return res.data;
    } catch (err) {
      console.error("❌ Failed to fetch course:", err);
      throw err;
    }
  };

  useEffect(() => {
    const fetchCourse = async () => {
      try {
        setLoading(true);
        const data = await getCourseById();
        setCourse(data.course);
        setIsEnrolled(data.isEnrolled);
        setTeacher(data.course.teacher);
        setIsTeacher(data.isTeacher);
        console.log("Course data fetched successfully:", data.course);
      } catch (err) {
        // Optionally show error toast
        console.error("❌ Error fetching course:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseIdOnly]);

  // Session timer
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionTime((prev) => {
        const newTime = prev + 1;

        // Every 60 seconds, update database
        if (newTime % 60 === 0) {
          const timeInMinutes = Math.floor(newTime / 60);
          console.log("Updating time in DB:", timeInMinutes);
          axios
            .put(
              `${host}/api/courses/${courseIdOnly}/update-time/${studentId}`,
              {
                role: user.role,
                time: timeInMinutes,
              }
            )
            .catch((err) => {
              console.error(err);
            });
        }

        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval); // Cleanup
  }, [courseIdOnly, studentId, user.role]);

  // Enhanced Xirsys ICE servers fetch function
  const getXirsysIceServers = async () => {
    try {
      console.log("🔄 Fetching ICE servers from Xirsys...");

      const response = await axios.put(
        "https://global.xirsys.net/_turn/AcademiaHub",
        {}, // Empty body for PUT request
        {
          headers: {
            Authorization:
              "Basic " +
              btoa("muhammadsaad349:5b90fda2-478a-11f0-8566-0242ac150003"),
            "Content-Type": "application/json",
          },
          timeout: 10000, // 10 second timeout
        }
      );

      if (
        response.data &&
        response.data.s === "ok" &&
        response.data.v &&
        response.data.v.iceServers
      ) {
        console.log(
          "✅ ICE servers fetched from Xirsys:",
          response.data.v.iceServers
        );
        return response.data.v.iceServers;
      } else {
        throw new Error("Invalid response structure from Xirsys");
      }
    } catch (err) {
      console.error(
        "❌ Failed to fetch ICE servers from Xirsys:",
        err.response?.data || err.message
      );

      // Enhanced fallback with multiple STUN servers
      return [
        { urls: "stun:stun.l.google.com:19302" },
        { urls: "stun:stun1.l.google.com:19302" },
        { urls: "stun:stun2.l.google.com:19302" },
        { urls: "stun:stun.services.mozilla.com" },
      ];
    }
  };

  // Initialize ICE servers
  useEffect(() => {
    const fetchIceServers = async () => {
      try {
        const servers = await getXirsysIceServers();
        setIceServers(servers);
        setIceServersLoaded(true);
        console.log("✅ ICE servers ready:", servers);
      } catch (error) {
        console.error("❌ Error initializing ICE servers:", error);
        // Set fallback servers
        setIceServers([
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun1.l.google.com:19302" },
        ]);
        setIceServersLoaded(true);
      }
    };

    fetchIceServers();
  }, []);

  const formatTime = (seconds) => {
    const hrs = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  const createPeerConnection = useCallback(
    (userId) => {
      if (!iceServersLoaded) {
        console.error("❌ ICE servers not loaded yet");
        return null;
      }

      console.log("🔗 Creating peer connection for:", userId);

      const peerConfig = {
        iceServers: iceServers,
        iceCandidatePoolSize: 10,
        iceTransportPolicy: "all",
        bundlePolicy: "max-bundle", // Better performance
        rtcpMuxPolicy: "require",
      };

      const peer = new RTCPeerConnection(peerConfig);

      // Enhanced ICE candidate handling with retry logic
      peer.onicecandidate = (event) => {
        if (event.candidate) {
          console.log(
            "🧊 Sending ICE candidate to:",
            userId,
            event.candidate.type
          );
          socket.emit("ice-candidate", {
            roomId: courseId,
            candidate: event.candidate,
            from: socket.id,
            to: userId,
          });
        }
      };

      // Enhanced connection state monitoring with automatic recovery
      peer.oniceconnectionstatechange = () => {
        console.log(
          `🧊 ICE connection state with ${userId}: ${peer.iceConnectionState}`
        );

        switch (peer.iceConnectionState) {
          case "failed":
            console.log(
              `❌ ICE connection failed with ${userId} - attempting restart`
            );
            setTimeout(() => {
              if (peer.iceConnectionState === "failed") {
                peer.restartIce();
              }
            }, 2000);
            break;
          case "disconnected":
            console.log(`⚠️ ICE disconnected with ${userId} - will retry`);
            // Auto-retry after 5 seconds
            setTimeout(() => {
              if (peer.iceConnectionState === "disconnected") {
                peer.restartIce();
              }
            }, 5000);
            break;
          case "closed":
            // Clean up peer reference
            if (peersRef.current[userId]) {
              delete peersRef.current[userId];
              setPeerStreams((prev) => {
                const newStreams = { ...prev };
                delete newStreams[userId];
                return newStreams;
              });
            }
            break;
        }
      };

      // Handle received tracks with better error handling
      peer.ontrack = (event) => {
        console.log("📹 Got track from:", userId, "Track:", event.track.kind);
        if (event.streams && event.streams[0]) {
          console.log("📺 Setting peer stream for:", userId);
          setPeerStreams((prev) => ({
            ...prev,
            [userId]: event.streams[0],
          }));
        }
      };

      return peer;
    },
    [iceServers, iceServersLoaded, courseId]
  );

  const toggleScreenShare = async () => {
    try {
      if (!isScreenSharing) {
        // Start screen sharing
        const screenStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            mediaSource: "screen",
            width: { ideal: 1920, max: 1920 },
            height: { ideal: 1080, max: 1080 },
            frameRate: { ideal: 30, max: 30 },
          },
          audio: true,
        });

        setScreenStream(screenStream);
        setIsScreenSharing(true);

        // Replace video track in all peer connections
        const videoTrack = screenStream.getVideoTracks()[0];
        Object.values(peersRef.current).forEach(async (peer) => {
          const sender = peer
            .getSenders()
            .find((s) => s.track && s.track.kind === "video");
          if (sender) {
            await sender.replaceTrack(videoTrack);
          }
        });

        // Update local video
        if (userVideo.current) {
          userVideo.current.srcObject = screenStream;
        }

        // Handle screen share end
        videoTrack.onended = () => {
          stopScreenShare();
        };
      } else {
        stopScreenShare();
      }
    } catch (error) {
      console.error("Error toggling screen share:", error);
    }
  };

  const stopScreenShare = async () => {
    if (screenStream) {
      screenStream.getTracks().forEach((track) => track.stop());
      setScreenStream(null);
    }
    setIsScreenSharing(false);

    // Replace with camera video track
    if (stream) {
      const videoTrack = stream.getVideoTracks()[0];
      Object.values(peersRef.current).forEach(async (peer) => {
        const sender = peer
          .getSenders()
          .find((s) => s.track && s.track.kind === "video");
        if (sender && videoTrack) {
          await sender.replaceTrack(videoTrack);
        }
      });

      // Update local video
      if (userVideo.current) {
        userVideo.current.srcObject = stream;
      }
    }
  };

  // Audio/Video controls
  const toggleMute = () => {
    if (stream) {
      stream.getAudioTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleVideo = () => {
    if (stream) {
      stream.getVideoTracks().forEach((track) => {
        track.enabled = !track.enabled;
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  // Function to handle disconnection
  const handleDisconnect = useCallback(async () => {
    console.log("Disconnecting from session...");

    // Clean up peers
    Object.values(peersRef.current).forEach((peer) => {
      if (peer && typeof peer.close === "function") {
        peer.close();
      }
    });

    // Clear state
    peersRef.current = {};
    setPeerStreams({});
    roomJoined.current = false;

    // Stop all media tracks
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }

    // Disconnect socket
    if (socket.connected) {
      socket.disconnect();
    }

    setConnected(false);

    // 👉 Time check for completing session
    const otherUserTime = sessionTime; // in seconds
    if (otherUserTime >= 3 * 60) {
      try {
        await axios.put(
          `${host}/api/courses/${courseIdOnly}/end-session/${studentId}`
        );
        console.log("Session marked as completed.");
      } catch (err) {
        console.error("Error ending session:", err);
      }
    }

    // Navigate after cleanup and DB update
    navigate(-1);
  }, [stream, sessionTime, navigate, courseIdOnly, studentId]);

  // Get user media first
  useEffect(() => {
    const getMedia = async () => {
      try {
        console.log("Getting user media...");
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        console.log("Media stream obtained successfully");
        setStream(mediaStream);

        if (userVideo.current) {
          userVideo.current.srcObject = mediaStream;
        }

        setIsReady(true);
      } catch (error) {
        console.error("Error accessing media devices:", error);
      }
    };

    getMedia();

    // Add event listener for beforeunload to handle page refresh/close
    const handleBeforeUnload = () => {
      if (socket.connected) {
        socket.disconnect();
      }

      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      // Clean up media stream
      if (stream) {
        console.log("Stopping media tracks");
        stream.getTracks().forEach((track) => track.stop());
      }

      // Disconnect socket
      if (socket.connected) {
        socket.disconnect();
      }

      // Clear event listener
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Enhanced initiatePeerConnection function
  const initiatePeerConnection = useCallback(
    (userId) => {
      console.log("🚀 Initiating peer connection to:", userId);

      // Don't create duplicate connections
      if (peersRef.current[userId]) {
        console.log("⚠️ Connection already exists for:", userId);
        return;
      }

      if (!stream) {
        console.error(
          "❌ Cannot initiate peer connection: stream is not available"
        );
        return;
      }

      if (!iceServersLoaded) {
        console.error("❌ ICE servers not loaded yet");
        return;
      }

      const peer = createPeerConnection(userId);
      if (!peer) return;

      // Store the peer first
      peersRef.current[userId] = peer;

      // Add our tracks
      try {
        stream.getTracks().forEach((track) => {
          console.log("➕ Adding track to peer connection:", track.kind);
          peer.addTrack(track, stream);
        });
      } catch (err) {
        console.error("❌ Error adding tracks to peer:", err);
      }

      // Create and send offer
      peer
        .createOffer({
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
        })
        .then((offer) => {
          console.log("📝 Created offer for:", userId);
          return peer.setLocalDescription(offer);
        })
        .then(() => {
          console.log("✅ Set local description for:", userId);
          socket.emit("offer", {
            roomId: courseId,
            offer: peer.localDescription,
            from: socket.id,
            to: userId,
          });
        })
        .catch((err) => {
          console.error("❌ Error creating/sending offer:", err);
          if (peersRef.current[userId]) {
            peersRef.current[userId].close();
            delete peersRef.current[userId];
          }
        });
    },
    [stream, courseId, iceServersLoaded, createPeerConnection]
  );

  // Enhanced handleOffer function
  const handleOffer = useCallback(
    async ({ offer, from }) => {
      console.log("📨 Received offer from:", from);

      if (from === socket.id) {
        console.log("⚠️ Ignoring own offer");
        return;
      }

      if (!stream) {
        console.error("❌ Cannot handle offer: stream is not available");
        return;
      }

      if (!iceServersLoaded) {
        console.error("❌ ICE servers not loaded yet");
        return;
      }

      // Close existing connection if any
      if (peersRef.current[from]) {
        console.log("🔄 Closing existing peer connection with:", from);
        peersRef.current[from].close();
        delete peersRef.current[from];
      }

      const peer = createPeerConnection(from);
      if (!peer) return;

      // Store the peer first
      peersRef.current[from] = peer;

      // Add our tracks
      try {
        stream.getTracks().forEach((track) => {
          console.log("➕ Adding track to answer peer:", track.kind);
          peer.addTrack(track, stream);
        });
      } catch (err) {
        console.error("❌ Error adding tracks to answer peer:", err);
      }

      // Set remote description and create answer
      try {
        console.log("📥 Setting remote description from:", from);
        await peer.setRemoteDescription(new RTCSessionDescription(offer));

        console.log("📝 Creating answer for:", from);
        const answer = await peer.createAnswer();

        console.log("✅ Setting local description for:", from);
        await peer.setLocalDescription(answer);

        console.log("📤 Sending answer to:", from);
        socket.emit("answer", {
          roomId: courseId,
          answer: peer.localDescription,
          from: socket.id,
          to: from,
        });
      } catch (err) {
        console.error("❌ Error creating/sending answer:", err);
        if (peersRef.current[from]) {
          peersRef.current[from].close();
          delete peersRef.current[from];
        }
      }
    },
    [stream, courseId, iceServersLoaded, createPeerConnection]
  );

  // Handle received answer
  const handleAnswer = useCallback(({ answer, from }) => {
    console.log("📨 Received answer from:", from);

    const peer = peersRef.current[from];
    if (!peer) {
      console.error("❌ No peer connection found for:", from);
      return;
    }

    if (peer.signalingState === "closed") {
      console.error(
        "❌ Cannot set remote description on closed connection for:",
        from
      );
      return;
    }

    console.log("📥 Setting remote description from answer for:", from);
    peer
      .setRemoteDescription(new RTCSessionDescription(answer))
      .then(() => {
        console.log("✅ Remote description set successfully for:", from);
      })
      .catch((err) => {
        console.error("❌ Error setting remote description:", err);
        if (peersRef.current[from]) {
          peersRef.current[from].close();
          delete peersRef.current[from];
        }
      });
  }, []);

  // Handle received ICE candidate
  const handleIceCandidate = useCallback(({ candidate, from }) => {
    console.log("🧊 Received ICE candidate from:", from, candidate.type);

    const peer = peersRef.current[from];
    if (!peer) {
      console.error("❌ No peer connection found for:", from);
      return;
    }

    if (peer.signalingState === "closed") {
      console.error(
        "❌ Cannot add ICE candidate on closed connection for:",
        from
      );
      return;
    }

    console.log("➕ Adding ICE candidate for:", from);
    peer
      .addIceCandidate(new RTCIceCandidate(candidate))
      .then(() => {
        console.log("✅ ICE candidate added successfully for:", from);
      })
      .catch((err) => {
        console.error("❌ Error adding ICE candidate:", err);
      });
  }, []);

  // Initialize socket and connections - wait for ICE servers
  useEffect(() => {
    if (!isReady || !stream || !iceServersLoaded) return;

    console.log("Initializing socket connection");

    // Clean up old listeners
    socket.off("connect");
    socket.off("user-connected");
    socket.off("offer");
    socket.off("answer");
    socket.off("ice-candidate");
    socket.off("receive-message");
    socket.off("user-disconnected");

    // Connect socket
    if (!socket.connected) {
      console.log("Connecting socket...");
      socket.connect();
    }

    // Join room on socket connection
    socket.on("connect", () => {
      console.log("Socket connected with ID:", socket.id);
      setMySocketId(socket.id);

      // Only join room once
      if (!roomJoined.current) {
        console.log("Joining room:", courseId);
        socket.emit("join-room", { roomId: courseId });
        roomJoined.current = true;
      }

      setConnected(true);
    });

    // Listen for new users
    socket.on("user-connected", (data) => {
      console.log("User connected:", data.userId);

      // Don't create connection to ourselves
      if (data.userId !== socket.id) {
        // Add a slight delay to make sure both peers are ready
        setTimeout(() => {
          initiatePeerConnection(data.userId);
        }, 1000);
      }
    });

    socket.on("offer", handleOffer);
    socket.on("answer", handleAnswer);
    socket.on("ice-candidate", handleIceCandidate);

    // Handle messages
    socket.on("receive-message", (data) => {
      if (data.userId !== socket.id) {
        console.log("Received message from:", data.userId);
        setMessages((prev) => [
          ...prev,
          {
            userId: data.userId.substring(0, 6),
            message: data.message,
            time: new Date(),
          },
        ]);
      }
    });

    socket.on("user-disconnected", (userId) => {
      console.log("User disconnected:", userId);

      // Close peer connection
      if (peersRef.current[userId]) {
        peersRef.current[userId].close();
        delete peersRef.current[userId];
      }

      // Remove video
      setPeerStreams((prev) => {
        const newPeerStreams = { ...prev };
        delete newPeerStreams[userId];
        return newPeerStreams;
      });
    });

    return () => {
      console.log("Cleaning up connections");

      // Clean up peers
      Object.values(peersRef.current).forEach((peer) => {
        if (peer && typeof peer.close === "function") {
          peer.close();
        }
      });

      // Clear state
      peersRef.current = {};
      setPeerStreams({});
      roomJoined.current = false;
    };
  }, [
    isReady,
    stream,
    iceServersLoaded,
    courseId,
    handleOffer,
    handleAnswer,
    handleIceCandidate,
    initiatePeerConnection,
  ]);

  // Send a message
  const sendMessage = () => {
    if (newMessage.trim()) {
      console.log("Sending message:", newMessage);

      socket.emit("send-message", {
        roomId: courseId,
        message: newMessage,
        userId: socket.id,
      });

      // Add our message to the UI
      setMessages((prev) => [
        ...prev,
        {
          userId: "Me",
          message: newMessage,
          time: new Date(),
        },
      ]);

      setNewMessage("");
    }
  };

  // Auto-scroll messages
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-indigo-900 to-purple-900">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">
                    {course?.title || "Live Session"}
                  </h1>
                  <p className="text-indigo-300 text-sm">
                    with {course?.teacher?.name}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* Session Timer */}
              <div className="bg-white/10 backdrop-blur-lg rounded-lg px-4 py-2 border border-white/20">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-white font-mono text-sm">
                    {formatTime(sessionTime)}
                  </span>
                </div>
              </div>

              {/* Connection Status */}
              <div className="bg-white/10 backdrop-blur-lg rounded-lg px-3 py-2 border border-white/20">
                {!iceServersLoaded ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full"></div>
                    <span className="text-yellow-400 text-sm">Loading...</span>
                  </div>
                ) : !connected ? (
                  <div className="flex items-center space-x-2">
                    <div className="animate-spin w-4 h-4 border-2 border-yellow-400 border-t-transparent rounded-full"></div>
                    <span className="text-yellow-400 text-sm">
                      Connecting...
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-green-400 text-sm">Connected</span>
                  </div>
                )}
              </div>

              {/* Chat Toggle */}
              {/* <button
                onClick={() => setShowChat(!showChat)}
                className="lg:hidden bg-white/10 backdrop-blur-lg rounded-lg p-3 border border-white/20 hover:bg-white/20 transition-all duration-200"
              >
                <svg
                  className="w-5 h-5 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </button> */}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6 h-[calc(100vh-140px)]">
          {/* Video Section */}
          <div
            className={`flex-1 ${
              showChat ? "lg:flex-1" : "w-full"
            } transition-all duration-300`}
          >
            {/* Pinned Video (Large) */}
            {pinnedVideo && (
              <div className="mb-6">
                <div className="relative bg-black/20 backdrop-blur-lg rounded-2xl overflow-hidden border border-white/20 shadow-2xl">
                  <video
                    ref={pinnedVideo === "self" ? userVideo : null}
                    autoPlay
                    muted={pinnedVideo === "self"}
                    playsInline
                    {...(pinnedVideo !== "self" && {
                      ref: (video) => {
                        if (
                          video &&
                          peerStreams[pinnedVideo] &&
                          video.srcObject !== peerStreams[pinnedVideo]
                        ) {
                          video.srcObject = peerStreams[pinnedVideo];
                          video.play().catch(console.error);
                        }
                      },
                    })}
                    className="w-full h-96 lg:h-[500px] object-cover"
                  />

                  {/* Pinned Video Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent">
                    <div className="absolute bottom-4 left-4 flex items-center space-x-3">
                      <div className="bg-black/70 backdrop-blur-lg rounded-lg px-3 py-2">
                        <span className="text-white font-medium">
                          {pinnedVideo === "self"
                            ? "You"
                            : `User ${pinnedVideo.substring(0, 6)}`}
                          {pinnedVideo === "self" &&
                            isScreenSharing &&
                            " (Screen)"}
                        </span>
                      </div>
                      <button
                        onClick={() => setPinnedVideo(null)}
                        className="bg-red-500/80 backdrop-blur-lg rounded-lg p-2 hover:bg-red-600/80 transition-all duration-200"
                      >
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Video Off Overlay for Pinned */}
                  {pinnedVideo === "self" && isVideoOff && (
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/90 to-purple-900/90 backdrop-blur-lg flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg
                            className="w-12 h-12 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <p className="text-white text-lg">Camera Off</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Video Grid */}
            <div
              className={`grid ${
                pinnedVideo ? "grid-cols-2" : "grid-cols-1 lg:grid-cols-2"
              } gap-4`}
            >
              {/* Your Video */}
              {(!pinnedVideo || pinnedVideo !== "self") && (
                <div className="group relative bg-black/20 backdrop-blur-lg rounded-xl overflow-hidden border border-white/20 hover:border-indigo-400/50 transition-all duration-300 shadow-xl">
                  <video
                    ref={userVideo}
                    autoPlay
                    muted
                    playsInline
                    className={`w-full ${
                      pinnedVideo ? "h-48" : "h-64 lg:h-80"
                    } object-cover`}
                  />

                  {/* Video Controls Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                      <div className="bg-black/70 backdrop-blur-lg rounded-lg px-3 py-1">
                        <span className="text-white text-sm font-medium">
                          You {isScreenSharing && "(Screen)"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Video Off Overlay */}
                  {isVideoOff && (
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/90 to-purple-900/90 backdrop-blur-lg flex items-center justify-center">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                          <svg
                            className="w-8 h-8 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                        <p className="text-white text-sm">Camera Off</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Peer Videos */}
              {Object.entries(peerStreams).map(
                ([userId, peerStream]) =>
                  (!pinnedVideo || pinnedVideo !== userId) && (
                    <div
                      key={userId}
                      className="group relative bg-black/20 backdrop-blur-lg rounded-xl overflow-hidden border border-white/20 hover:border-indigo-400/50 transition-all duration-300 shadow-xl"
                    >
                      <video
                        autoPlay
                        playsInline
                        muted={false}
                        ref={(video) => {
                          if (
                            video &&
                            peerStream &&
                            video.srcObject !== peerStream
                          ) {
                            video.srcObject = peerStream;
                            video.play().catch(console.error);
                          }
                        }}
                        onLoadedMetadata={(e) => {
                          e.target.play().catch(console.error);
                        }}
                        className={`w-full ${
                          pinnedVideo ? "h-48" : "h-64 lg:h-80"
                        } object-cover`}
                      />

                      {/* Video Controls Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                          <div className="bg-black/70 backdrop-blur-lg rounded-lg px-3 py-1">
                            <span className="text-white text-sm font-medium">
                              User {userId.substring(0, 6)}
                            </span>
                          </div>

                          <button
                            onClick={() => setPinnedVideo(userId)}
                            className="bg-white/20 backdrop-blur-lg rounded-lg p-2 hover:bg-white/30 transition-all duration-200"
                          >
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  )
              )}

              {/* Empty State */}
              {Object.keys(peerStreams).length === 0 && !pinnedVideo && (
                <div className="bg-white/5 backdrop-blur-lg rounded-xl border-2 border-dashed border-white/20 h-64 lg:h-80 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-white/60"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                      </svg>
                    </div>
                    <p className="text-white/70 text-sm">
                      Waiting for others to join...
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="mt-6 flex justify-center">
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={toggleMute}
                    className={`relative p-4 rounded-xl transition-all duration-200 ${
                      isMuted
                        ? "bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/25"
                        : "bg-white/20 hover:bg-white/30 border border-white/30"
                    }`}
                  >
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      {isMuted ? (
                        // Muted Microphone (mic with cross)
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5a3 3 0 116 0v6a3 3 0 11-6 0V5zm9.243 2.757L18 9m0 0l2 2m-2-2l-2 2m2-2l2-2M5 10v1a7 7 0 0014 0v-1"
                        />
                      ) : (
                        // Normal Microphone
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5a3 3 0 116 0v6a3 3 0 11-6 0V5zm7 6a5 5 0 01-10 0M12 19v2m-4 0h8"
                        />
                      )}
                    </svg>
                  </button>

                  <button
                    onClick={toggleVideo}
                    className={`relative p-4 rounded-xl transition-all duration-200 ${
                      isVideoOff
                        ? "bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/25"
                        : "bg-white/20 hover:bg-white/30 border border-white/30"
                    }`}
                  >
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      {isVideoOff ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      )}
                    </svg>
                  </button>

                  {/* toggle chat button */}

                  {/* Controls section mein ye add karo */}
                  <button
                    onClick={() => setShowChat(!showChat)}
                    className={`relative p-4 rounded-xl transition-all duration-200 ${
                      showChat
                        ? "bg-indigo-500 hover:bg-indigo-600 shadow-lg shadow-blue-500/25"
                        : "bg-white/20 hover:bg-white/30 border border-white/30"
                    }`}
                  >
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={toggleScreenShare}
                    className={`relative p-4 rounded-xl transition-all duration-200 ${
                      isScreenSharing
                        ? "bg-green-500 hover:bg-green-600 shadow-lg shadow-green-500/25"
                        : "bg-white/20 hover:bg-white/30 border border-white/30"
                    } hidden md:block`}
                  >
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </button>

                  <div className="w-px h-8 bg-white/20"></div>

                  <button
                    onClick={handleDisconnect}
                    className="relative p-4 rounded-xl bg-red-500 hover:bg-red-600 transition-all duration-200 shadow-lg shadow-red-500/25"
                  >
                    <svg
  className="w-6 h-6 text-white"
  fill="none"
  stroke="currentColor"
  viewBox="0 0 24 24"
>
  <path
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={2}
    d="M3 5l18 18M21 15v4a2 2 0 01-2.18 2 19.77 19.77 0 01-8.63-3.06A19.94 19.94 0 013 8.28a2 2 0 012-2h4m5.2 5.2a10.94 10.94 0 015.8 5.8"
  />
</svg>

                    
                    {/* <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16.72 11.06A10.94 10.94 0 0119 12a10.94 10.94 0 01-2.28.94m0 0A10.824 10.824 0 0112 15c-1.66 0-3.23-.39-4.72-1.06m9.44-2.88A10.824 10.824 0 0012 9c1.66 0 3.23.39 4.72 1.06M3 3l18 18m-9-9v6"
                      />
                    </svg> */}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Chat Section */}
          {/* <div
            className={`${
              showChat ? "block" : "hidden lg:block"
            } w-full lg:w-80 transition-all duration-300`}
          > */}
          <div
            className={`${
              showChat ? "block" : "hidden"
            } fixed lg:relative bottom-0 lg:bottom-auto left-0 lg:left-auto right-0 lg:right-auto w-full lg:w-80 h-96 lg:h-full z-50 lg:z-auto transition-all duration-300`}
          >
            {/* <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 h-full flex flex-col shadow-xl"> */}
            <div className="bg-white/10 backdrop-blur-lg rounded-t-2xl lg:rounded-2xl border border-white/20 h-full flex flex-col shadow-xl mx-4 lg:mx-0 mb-4 lg:mb-0">
              <div className="p-6 border-b border-white/20">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white">Chat</h3>
                  <button
                    onClick={() => setShowChat(false)}
                    className="lg:hidden bg-white/20 rounded-lg p-2 hover:bg-white/30 transition-all duration-200"
                  >
                    <svg
                      className="w-4 h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg
                        className="w-6 h-6 text-white/60"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                      </svg>
                    </div>
                    <p className="text-white/60 text-sm">No messages yet</p>
                    <p className="text-white/40 text-xs mt-1">
                      Start the conversation!
                    </p>
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <div
                      key={index}
                      className={`flex ${
                        msg.userId === "Me" ? "justify-end" : "justify-start"
                      }`}
                    >
                      <div
                        className={`max-w-xs rounded-2xl p-4 ${
                          msg.userId === "Me"
                            ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25"
                            : "bg-white/10 backdrop-blur-lg text-white border border-white/20"
                        }`}
                      >
                        <div className="text-xs opacity-75 mb-2 font-medium">
                          {msg.userId}
                        </div>
                        <div className="text-sm leading-relaxed">
                          {msg.message}
                        </div>
                        <div className="text-xs opacity-50 mt-2">
                          {msg.time.toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              <div className="p-4 border-t border-white/20">
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                    placeholder="Type your message..."
                    className="flex-1 bg-white/10 backdrop-blur-lg text-white placeholder-white/60 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500 border border-white/20"
                  />
                  <button
                    onClick={sendMessage}
                    disabled={!newMessage.trim()}
                    className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 disabled:from-gray-500 disabled:to-gray-600 disabled:cursor-not-allowed text-white p-3 rounded-xl transition-all duration-200 shadow-lg shadow-indigo-500/25"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveSession;
